import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Send, CheckCircle, AlertCircle, Loader2, HelpCircle, MessageCircle } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export default function TelegramSettings() {
  const [settings, setSettings] = useState({
    configured: false,
    enabled: false,
    bot_token: '',
    chat_id: '',
    bot_token_masked: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [newToken, setNewToken] = useState('');
  const [newChatId, setNewChatId] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/admin/telegram/settings');
      setSettings(response.data);
      setNewChatId(response.data.chat_id || '');
    } catch (error) {
      console.error('Error loading Telegram settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // If not configured yet, require new token
    if (!newToken && !settings.configured) {
      toast.error('Bitte geben Sie einen Bot-Token ein');
      return;
    }
    if (!newChatId) {
      toast.error('Bitte geben Sie eine Chat-ID ein');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        chat_id: newChatId,
        enabled: true  // Enable when saving
      };
      
      // Only send bot_token if a new one was entered
      if (newToken) {
        payload.bot_token = newToken;
      } else if (settings.configured) {
        // Keep existing token - send a special marker
        payload.bot_token = "__KEEP_EXISTING__";
      }
      
      await api.post('/admin/telegram/settings', payload);
      toast.success('Telegram-Einstellungen gespeichert!');
      setNewToken('');
      setShowToken(false);
      await loadSettings();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await api.post('/admin/telegram/test');
      toast.success('Test-Nachricht gesendet! Prüfen Sie Telegram.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  const handleToggleEnabled = async (enabled) => {
    setSettings(prev => ({ ...prev, enabled }));
    
    if (settings.configured) {
      try {
        await api.post('/admin/telegram/settings', {
          bot_token: settings.bot_token_masked,
          chat_id: settings.chat_id,
          enabled
        });
        toast.success(enabled ? 'Telegram aktiviert' : 'Telegram deaktiviert');
      } catch (error) {
        setSettings(prev => ({ ...prev, enabled: !enabled }));
        toast.error('Fehler beim Speichern');
      }
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="telegram-settings">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-[#0088cc]" />
              Telegram-Benachrichtigungen
            </CardTitle>
            <CardDescription className="mt-1">
              Erhalten Sie Terminerinnerungen direkt auf Ihr Handy
            </CardDescription>
          </div>
          <Badge variant={settings.configured && settings.enabled ? "default" : "secondary"} 
                 className={settings.configured && settings.enabled ? "bg-green-500" : ""}>
            {settings.configured ? (settings.enabled ? "Aktiv" : "Deaktiviert") : "Nicht konfiguriert"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Toggle */}
        {settings.configured && (
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium text-sm">Benachrichtigungen aktiviert</p>
              <p className="text-xs text-gray-500">Erhalten Sie Erinnerungen per Telegram</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={handleToggleEnabled}
              data-testid="telegram-toggle"
            />
          </div>
        )}

        {/* Reminder Info */}
        <div className="bg-blue-50 rounded-lg p-4">
          <h4 className="font-medium text-sm mb-2 text-blue-800">Sie werden benachrichtigt:</h4>
          <ul className="space-y-1 text-sm text-blue-700">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-blue-500" />
              1 Tag vor dem Termin
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-blue-500" />
              45 Minuten vor dem Termin
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-blue-500" />
              20 Minuten vor dem Termin
            </li>
          </ul>
        </div>

        {/* Configuration Form */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bot-token" className="flex items-center gap-2">
              Bot-Token
              <a 
                href="https://t.me/BotFather" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline text-xs"
              >
                (von @BotFather)
              </a>
            </Label>
            {settings.configured && !showToken ? (
              <div className="flex gap-2">
                <Input
                  value={settings.bot_token_masked}
                  disabled
                  className="flex-1 font-mono text-sm"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowToken(true)}
                >
                  Ändern
                </Button>
              </div>
            ) : (
              <Input
                id="bot-token"
                type="password"
                placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                value={newToken}
                onChange={(e) => setNewToken(e.target.value)}
                className="font-mono text-sm"
                data-testid="telegram-token-input"
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="chat-id" className="flex items-center gap-2">
              Chat-ID
              <span className="text-xs text-gray-500">(Ihre persönliche oder Gruppen-ID)</span>
            </Label>
            <Input
              id="chat-id"
              type="text"
              placeholder="123456789"
              value={newChatId}
              onChange={(e) => setNewChatId(e.target.value)}
              className="font-mono text-sm"
              data-testid="telegram-chatid-input"
            />
          </div>

          {/* Help Section */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
              <HelpCircle className="w-4 h-4" />
              So finden Sie Ihre Chat-ID:
            </h4>
            <ol className="text-xs text-gray-600 space-y-1 list-decimal list-inside">
              <li>Öffnen Sie Telegram und suchen Sie nach <code className="bg-gray-200 px-1 rounded">@userinfobot</code></li>
              <li>Starten Sie einen Chat mit dem Bot</li>
              <li>Der Bot zeigt Ihnen Ihre Chat-ID an</li>
              <li>Kopieren Sie die Zahl und fügen Sie sie oben ein</li>
            </ol>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary flex items-center gap-2"
            data-testid="telegram-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Speichern
          </Button>
          
          {settings.configured && (
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={testing || !settings.enabled}
              className="flex items-center gap-2"
              data-testid="telegram-test-btn"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Test senden
            </Button>
          )}
        </div>

        {/* Warning if not enabled */}
        {settings.configured && !settings.enabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-amber-700 text-sm">
              <AlertCircle className="w-4 h-4 inline mr-1" />
              Telegram ist deaktiviert. Aktivieren Sie es, um Benachrichtigungen zu erhalten.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
